import { search } from '@/clients/customer/restaurants';
import { RestaurantTag } from '@/lib/types/customer/RestaurantTags';
import { useRestaurantTagsDataStore } from '@/stores/cutomer/restaurantTags';
import { useRestaurantStore } from '@/stores/restaurants';
import useAppLocale from './useAppLocale';
import { useParams, usePathname, useRouter } from 'next/navigation';

export default function useRestaurantTags() {
  const pathname = usePathname();
  const nextParams = useParams();
  const router = useRouter();
  const {
    tagsData,
    setSelectedTag,
    setChildrenTags,
    setShowChildrenTag,
    setSelectedSearchableTags,
    resetSearchableTags,
    childrenTags,
  } = useRestaurantTagsDataStore();

  const { locale } = useAppLocale();

  const { setData, setIsLoading } = useRestaurantStore();

  const getSearchParams = () => {
    if (window !== undefined) {
      const url = new URL(window.location.href);
      return Object.fromEntries(url.searchParams.entries());
    }
  };

  const getParamsTagIds = (paramStr?: string) => {
    if (paramStr) {
      return paramStr?.split(',').map((item) => parseInt(item));
    }

    return getSearchParams()
      ?.tag_ids?.split(',')
      .map((item) => parseInt(item));
  };

  const updateSearchParams = (key: string, value?: string) => {
    const currentParams = new URLSearchParams(window.location.search);
    if (value) {
      currentParams.set(key, value);
    } else {
      currentParams.delete(key);
    }
    router.replace(`${pathname}?${currentParams.toString()}`, {
      scroll: false,
    });
  };

  const fetchFilterData = async (params: Record<string, any> = {}) => {
    setIsLoading(true);
    let queryParams = { ...getSearchParams(), ...params };
    if (pathname.includes('/city/') && nextParams?.slug) {
      queryParams = { ...queryParams, city: nextParams.slug };
    }

    try {
      const response = await search(
        {
          ...queryParams,
        },
        {
          ['Accept-Language']: locale || '',
        }
      );

      setData(response.data);
    } catch (error) {
      // console.error('Error fetching data:', error);
    }
    setIsLoading(false);
  };

  const searchWithParentTag = (tag?: RestaurantTag, forAll?: boolean) => {
    resetSearchableTags();
    if (!tag) {
      updateSearchParams('tag_ids');
      // sParams = getSearchParams();
      // fetchFilterData(sParams);
      return;
    }

    setSelectedSearchableTags(tag);
    const sParams = getSearchParams();
    const paramsTagIds = getParamsTagIds(sParams?.tag_ids);
    let isExists = paramsTagIds?.find((item) => item === tag.id);
    const findChildTagById = childrenTags?.find(
      (item) => item.id === paramsTagIds?.[0]
    );
    if (findChildTagById) {
      isExists = findChildTagById?.parent_id === tag.id ? 1 : 0;
    }

    if (forAll && !paramsTagIds?.includes(tag.id)) {
      updateSearchParams('tag_ids', tag?.id.toString());
    } else if (isExists) {
      updateSearchParams('tag_ids');
      setSelectedTag(null);
      setChildrenTags([]);
      setShowChildrenTag(false);
    } else {
      updateSearchParams('tag_ids', tag?.id.toString());
      setSelectedTag(tag);
      if (tag.children?.length > 0) {
        setChildrenTags(tag.children);
        setShowChildrenTag(true);
      } else {
        setChildrenTags([]);
        setShowChildrenTag(false);
      }
    }

    // sParams = getSearchParams();
    // fetchFilterData(sParams);
  };

  const searchWithChildTag = ({
    tag,
    vendorType,
    allowMultipleSelection,
  }: {
    tag?: RestaurantTag;
    vendorType?: string;
    allowMultipleSelection?: boolean;
  }) => {
    let sParams;
    let updatedParamTagIds = [];
    if (!tag) {
      updateSearchParams('tag_ids');
      // sParams = getSearchParams();
      // fetchFilterData(sParams);
    } else {
      sParams = getSearchParams();
      const paramsTagIds = getParamsTagIds(sParams?.tag_ids);
      if (paramsTagIds && paramsTagIds?.length > 0) {
        const isExists = paramsTagIds?.find((item) => item === tag.id);
        const parentTagIds = tagsData?.data?.map((item) => item.id);
        const onlyChildren = paramsTagIds.filter(
          (item) => !parentTagIds?.includes(item)
        );
        // if already exists remove it
        if (isExists && onlyChildren?.length === 1) {
          updatedParamTagIds = [tag.parent_id];
        } else if (isExists) {
          const filteredSearchableTags = onlyChildren?.filter(
            (item) => item !== tag.id
          );
          updatedParamTagIds = filteredSearchableTags;
        } else if (allowMultipleSelection) {
          // if not exists append it
          updatedParamTagIds = [...onlyChildren, tag.id];
        } else {
          // if not exists replace it
          updatedParamTagIds = [tag.id];
        }
      } else {
        // if there is no active tag then add it
        updatedParamTagIds = [tag.id];
      }
      if (updatedParamTagIds?.length > 0) {
        const tagIds = updatedParamTagIds.join(',');
        updateSearchParams('tag_ids', tagIds);
      } else {
        updateSearchParams('tag_ids');
      }
    }

    // sParams = getSearchParams();
    // fetchFilterData(sParams);
  };

  // Initialize and store the Swiper instance
  const swiperRefs = useRestaurantTagsDataStore((state) => state.swiperRefs);

  // Navigate to a specific slide in a specific Swiper instance
  const scrollToSlide = (id: string, slideIndex: number) => {
    const swiper = swiperRefs.get(id);
    if (swiper) {
      swiper.slideTo(slideIndex);
    }
  };

  return {
    searchWithParentTag,
    searchWithChildTag,
    scrollToSlide,
    getParamsTagIds,
    fetchFilterData,
    getSearchParams,
  };
}
